import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import {
  ChevronLeft, ChevronRight, X, Loader2, AlertCircle, Ban, Sparkles,
} from 'lucide-react';
import './calendar/adminCalendar.css';
import { useUserPreference } from '../hooks/useUserPreference';
import { buildItems, layout, maxLanesFor, minToTime, timeToMin } from './calendar/calendarLayout';
import {
  CAL_TYPES, CAL_TYPE_ORDER, calType, typeVars,
  type BookableType, type CalItem, type CalSession,
} from './calendar/calendarTypes';
import { hourWindow, staffNamer, toCalSession, tzNow } from './calendar/calendarAdapter';
import type { ApiSession } from './calendar/calendarAdapter';
import { EventCard, MoreButton, MorePopover, RunCard, laneStyle, type Geometry } from './calendar/TimelineParts';
import { BranchSelect, BusyPill, Segmented, TypeFilter } from './calendar/CalendarControls';
import { SessionDrawer } from './calendar/SessionDrawer';
import { TodayDayStrip } from './calendar/TodayDayStrip';
import { TodaySide } from './calendar/TodaySide';
import { offShiftGaps, onShiftAt, roleLabel, type DayRoster, type RosterStaff } from './calendar/todayRoster';

// ─── Types (structurally compatible with SchedulePage's ClassSession) ──────────

export interface DaySheetSession {
  id: string;
  classId: string;
  branchId: string;
  gymId: string;
  trainerId: string | null;
  roomId: string | null;
  recurringTemplateId: string | null;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  bookedCount: number;
  waitlistCount: number;
  status: string;
  sourceMode?: string;
  onlineLink: string | null;
  notes: string | null;
  class: { id: string; name: string; bookableType?: BookableType; category: string | null; level: string | null; staffRequired?: boolean };
  branch: { id: string; name: string; displayName: string };
  room: { id: string; name: string } | null;
  service: { id: string; name: string; defaultPrice: number } | null;
  _count: { bookings: number; waitlist: number };
}

export interface QuickBookPrefill {
  classId?: string;
  branchId?: string;
  date?: string;
  startTime?: string;
  staffId?: string;
  roomId?: string;
}

interface DaySheetTrainer {
  id: string;
  name: string | null;
  displayName: string | null;
  role?: string;
  /** Every branch the provider works at (from /trainer-availability/trainers) */
  branchIds?: string[];
}

interface DaySheetBranch {
  id: string;
  name: string;
  displayName: string;
  gymId?: string;
}

interface DaySheetRoom {
  id: string;
  name: string;
  kind?: string;
  branchId: string;
}

interface DaySheetClass {
  id: string;
  gymId?: string;
  name: string;
  bookableType?: BookableType;
  scheduleMode?: 'timetable' | 'appointments' | 'grid';
  isActive?: boolean;
}

interface BlockedTimeRow {
  id: string;
  gymId: string;
  branchId: string | null;
  staffId: string | null;
  roomId: string | null;
  date: string;
  startTime: string;
  endTime: string;
  reason: string | null;
}

interface DayColumn {
  key: string;
  /** What the column stands for, which decides its header and its rules:
   *  `unassigned` sessions that need a coach and have none (red, staff axis),
   *  `staff` one provider, `self` the provider-less bookables, `room` one
   *  resource, `noroom` sessions with no room set. */
  kind: 'unassigned' | 'staff' | 'self' | 'room' | 'noroom';
  label: string;
  sub: string;
  /** Branch this column belongs to — set while viewing all branches at once */
  branchId?: string;
  /** null = the explicit "Unassigned" column on the staff axis */
  staffId?: string | null;
  /** null = the explicit "No room" column on the rooms axis */
  roomId?: string | null;
  /** Rooms axis: the type it mostly runs, for the header dot. */
  type?: BookableType;
  /** Staff axis: their shifts today, for the hatch and the "on shift" badge. */
  staff?: RosterStaff;
  onShift?: boolean;
  /** Self-serve holds several courts at once, so it gets a wider track. */
  wide?: boolean;
}

interface DragState {
  sessionId: string;
  durationMin: number;
  origStartMin: number;
  origColIdx: number;
  startClientX: number;
  startClientY: number;
  grabOffsetY: number;
  curStartMin: number;
  curColIdx: number;
  moved: boolean;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

/** Default window, widened by hourWindow() to fit anything scheduled outside it. */
const DEFAULT_HOURS: [number, number] = [6, 22];
/** Row density, shared with the full timeline modal: pixels per hour. */
const DENSITY = { Compact: 48, Default: 72, Roomy: 104 } as const;
type Density = keyof typeof DENSITY;
/** Gutter above/below the grid: axis labels are centred on their line, so the
 *  first (06:00) and last one would otherwise be sliced in half. */
const GRID_GUTTER = 12;
const AXIS_WIDTH = 56;
const MIN_COL_WIDTH = 190;
/** Column-track share of the Self-serve column, from the handoff's 1.4fr. */
const SELF_COL_WEIGHT = 1.4;
/** Find-next-available panel, in px — it is positioned, so this is not CSS. */
const PANEL_WIDTH = 320;
/** Derived columns — not resources, so never a drop target for an id. */
const COL_UNASSIGNED = '__unassigned';
const COL_SELF = '__self';
const COL_NOROOM = '__noroom';
const DROP_SNAP_MIN = 5;
const CLICK_SNAP_MIN = 15;

const HATCH_STYLE: React.CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(45deg, rgba(107,114,128,0.15) 0px, rgba(107,114,128,0.15) 6px, transparent 6px, transparent 12px)',
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysStr(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  return localDateStr(dt);
}

function fmtDateLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}

async function authHeaders(): Promise<Record<string, string> | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;
  return { Authorization: `Bearer ${session.access_token}`, 'x-client-type': 'web' };
}

// ─── Block time modal ──────────────────────────────────────────────────────────

function BlockTimeModal({ gymId, branchId, dateStr, staff, rooms, defaultTarget, onClose, onSaved }: {
  gymId: string;
  branchId: string;
  dateStr: string;
  staff: DaySheetTrainer[];
  rooms: DaySheetRoom[];
  defaultTarget: 'staff' | 'room';
  onClose: () => void;
  onSaved: () => void;
}) {
  const [target, setTarget] = useState<'staff' | 'room'>(defaultTarget);
  const [targetId, setTargetId] = useState('');
  const [date, setDate] = useState(dateStr);
  const [startTime, setStartTime] = useState('12:00');
  const [endTime, setEndTime] = useState('13:00');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const options = target === 'staff' ? staff.map(s => ({ id: s.id, label: s.displayName || s.name || 'Staff' }))
    : rooms.map(r => ({ id: r.id, label: r.name }));

  const submit = async () => {
    if (!targetId) return void toast.error(target === 'staff' ? 'Pick a staff member' : 'Pick a room');
    if (!startTime || !endTime || endTime <= startTime) return void toast.error('End time must be after start time');
    setSaving(true);
    try {
      const headers = await authHeaders();
      if (!headers) return;
      const res = await fetch(`${API_BASE_URL}/api/blocked-time`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gymId,
          branchId: branchId || undefined,
          ...(target === 'staff' ? { staffId: targetId } : { roomId: targetId }),
          date,
          startTime,
          endTime,
          reason: reason.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to block time');
      toast.success('Time blocked');
      onSaved();
      onClose();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to block time');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-5 space-y-3.5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Ban className="w-4 h-4 text-gray-500" />
            Block time
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="flex items-center gap-0.5 bg-gray-100 rounded-xl p-1">
          {(['staff', 'room'] as const).map(t => (
            <button
              key={t}
              onClick={() => { setTarget(t); setTargetId(''); }}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg capitalize transition-colors ${target === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {t === 'staff' ? 'Staff' : 'Room / Court'}
            </button>
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{target === 'staff' ? 'Staff *' : 'Room *'}</label>
          <select
            value={targetId}
            onChange={e => setTargetId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
          >
            <option value="">Select…</option>
            {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
            <input
              type="time"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To *</label>
            <input
              type="time"
              value={endTime}
              onChange={e => setEndTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Lunch, Maintenance"
            maxLength={120}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Block
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Find next available popover ───────────────────────────────────────────────

interface NextSlot {
  date: string;
  startTime: string;
  detail?: string;
  roomId?: string;
}

function FindNextPopover({ classes, gymId, branchId, dateStr, timezone, rect, onPickSlot, onPickSession, onClose }: {
  classes: DaySheetClass[];
  gymId: string;
  branchId: string;
  dateStr: string;
  timezone?: string;
  /** The trigger button, so the panel can anchor to it. */
  rect: DOMRect;
  onPickSlot: (prefill: QuickBookPrefill) => void;
  onPickSession: (session: DaySheetSession) => void;
  onClose: () => void;
}) {
  const active = classes.filter(c => c.isActive !== false);
  const [classId, setClassId] = useState(active[0]?.id || '');
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState<NextSlot[] | null>(null);
  const [sessions, setSessions] = useState<DaySheetSession[] | null>(null);
  const [searchedMode, setSearchedMode] = useState<'timetable' | 'appointments' | 'grid'>('timetable');

  const modeFor = (c?: DaySheetClass): 'timetable' | 'appointments' | 'grid' => {
    if (c?.scheduleMode) return c.scheduleMode;
    if (c?.bookableType === 'facility') return 'grid';
    if (c && ['spa', 'pt', 'consult'].includes(c.bookableType || '')) return 'appointments';
    return 'timetable';
  };

  const search = async () => {
    const cls = active.find(c => c.id === classId);
    if (!cls) return void toast.error('Pick a bookable');
    const mode = modeFor(cls);
    setSearchedMode(mode);
    setLoading(true);
    setSlots(null);
    setSessions(null);
    try {
      const headers = await authHeaders();
      if (!headers) return;
      const now = tzNow(timezone);

      if (mode === 'timetable') {
        const res = await fetch(
          `${API_BASE_URL}/api/class-sessions?classId=${cls.id}${gymId ? `&gymId=${gymId}` : ''}${branchId ? `&branchId=${branchId}` : ''}&dateFrom=${dateStr}&dateTo=${addDaysStr(dateStr, 7)}&status=scheduled`,
          { headers }
        );
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load sessions');
        const upcoming = (data.data as DaySheetSession[])
          .filter(s => s.bookedCount < s.capacity)
          .filter(s => {
            const sDate = s.date.slice(0, 10);
            return sDate > now.dateStr || (sDate === now.dateStr && timeToMin(s.startTime) >= now.min);
          })
          .slice(0, 6);
        setSessions(upcoming);
      } else {
        if (!branchId) {
          toast.error('Pick a single branch to search appointment or court availability');
          return;
        }
        const found: NextSlot[] = [];
        for (const d of [dateStr, addDaysStr(dateStr, 1)]) {
          if (found.length >= 6) break;
          const res = await fetch(
            `${API_BASE_URL}/api/class-bookings/availability?classId=${cls.id}&branchId=${branchId}&date=${d}`,
            { headers }
          );
          const data = await res.json();
          if (!res.ok || !data.success) continue;
          const isToday = d === now.dateStr;
          if (mode === 'grid') {
            const courts: Array<{ id: string; name: string; cells: Array<{ startTime: string; available: boolean }> }> = data.data.courts || [];
            const cellCount = courts[0]?.cells.length || 0;
            for (let i = 0; i < cellCount && found.length < 6; i++) {
              const t = courts[0]?.cells[i]?.startTime;
              if (!t || (isToday && timeToMin(t) < now.min)) continue;
              const court = courts.find(c => c.cells[i]?.available);
              if (court) found.push({ date: d, startTime: t, detail: court.name, roomId: court.id });
            }
          } else {
            const times: Array<{ startTime: string }> = data.data.times || [];
            for (const t of times) {
              if (found.length >= 6) break;
              if (isToday && timeToMin(t.startTime) < now.min) continue;
              found.push({ date: d, startTime: t.startTime });
            }
          }
        }
        setSlots(found);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  /* Anchored to the button and clamped to the viewport rather than hung off it
     with `right-0`: the toolbar wraps at narrow widths, and a panel that opens
     leftwards from a button near the left edge lands on the app sidebar. Fixed
     and in the calendar's popover band so it is above that sidebar (z-100) and
     its dropdowns (z-120), like the "+N" popover. */
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8));
  const top = Math.min(rect.bottom + 8, Math.max(8, window.innerHeight - 220));

  return (
    <>
      <div className="acal-scrim0" onClick={onClose} />
      <div
        className="fixed bg-white border border-gray-200 rounded-2xl shadow-xl p-4 space-y-3 overflow-auto"
        style={{ left, top, width: PANEL_WIDTH, maxHeight: `calc(100vh - ${top + 16}px)`, zIndex: 1011 }}
        role="dialog"
        aria-label="Find next available"
      >
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
          Find next available
        </p>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
          <X className="w-3.5 h-3.5 text-gray-500" />
        </button>
      </div>

      <div className="flex gap-2">
        <select
          value={classId}
          onChange={e => { setClassId(e.target.value); setSlots(null); setSessions(null); }}
          className="flex-1 min-w-0 px-2.5 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white"
        >
          {active.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button
          onClick={search}
          disabled={loading || !classId}
          className="px-3 py-2 text-xs font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-700 transition-colors disabled:opacity-50 flex items-center gap-1.5 shrink-0"
        >
          {loading && <Loader2 className="w-3 h-3 animate-spin" />}
          Search
        </button>
      </div>

      {sessions && (
        sessions.length === 0 ? (
          <p className="text-xs text-gray-400">No upcoming scheduled sessions with spots in the next 7 days.</p>
        ) : (
          <div className="space-y-1">
            {sessions.map(s => (
              <button
                key={s.id}
                onClick={() => { onPickSession(s); onClose(); }}
                className="w-full text-left px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-400 transition-colors flex items-center justify-between gap-2"
              >
                <span className="text-xs font-semibold text-gray-800">{fmtDateLabel(s.date.slice(0, 10))} · {s.startTime}</span>
                <span className="text-[10px] text-gray-400">{s.capacity - s.bookedCount} left</span>
              </button>
            ))}
          </div>
        )
      )}

      {slots && (
        slots.length === 0 ? (
          <p className="text-xs text-gray-400">No free {searchedMode === 'grid' ? 'court slots' : 'times'} today or tomorrow.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {slots.map((sl, i) => (
              <button
                key={i}
                onClick={() => {
                  onPickSlot({ classId, branchId, date: sl.date, startTime: sl.startTime, roomId: sl.roomId });
                  onClose();
                }}
                className="px-2.5 py-1 rounded-lg border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 text-xs font-semibold text-gray-700 transition-colors"
                title={sl.detail}
              >
                {fmtDateLabel(sl.date)} {sl.startTime}{sl.detail ? ` · ${sl.detail}` : ''}
              </button>
            ))}
          </div>
        )
      )}
      </div>
    </>
  );
}

// ─── BookingDaySheet ───────────────────────────────────────────────────────────

export default function BookingDaySheet({ gymId, branches, trainers, rooms, classes, types, onTypesChange, refreshSignal, onSessionClick, onQuickBook }: {
  gymId: string;
  branches: DaySheetBranch[];
  trainers: DaySheetTrainer[];
  rooms: DaySheetRoom[];
  classes: DaySheetClass[];
  /** Type filter, shared with the Calendar tab — filtering is one act. */
  types: BookableType[];
  onTypesChange: (t: BookableType[]) => void;
  refreshSignal: number;
  onSessionClick: (session: DaySheetSession) => void;
  onQuickBook: (prefill?: QuickBookPrefill) => void;
}) {
  const [dateStr, setDateStr] = useState(() => localDateStr(new Date()));
  /** '' = all branches */
  const [branchId, setBranchId] = useState('');
  /* Which resource axis and how tall an hour is follow the user, not the
     browser — the same preference store the Calendar tab uses. */
  const [prefs, setPrefs] = useUserPreference('bookings_today', {
    axis: 'staff' as 'staff' | 'rooms',
    density: 'Default' as Density,
  });
  const axis = prefs.axis;

  const [sessions, setSessions] = useState<DaySheetSession[]>([]);
  const [blocked, setBlocked] = useState<BlockedTimeRow[]>([]);
  const [branchStaff, setBranchStaff] = useState<DaySheetTrainer[] | null>(null);
  const [roster, setRoster] = useState<DayRoster | null>(null);
  const [timezone, setTimezone] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Anything in flight — background refreshes, saves, deletes. */
  const [busyCount, setBusyCount] = useState(0);
  const busy = busyCount > 0;
  const track = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    setBusyCount(c => c + 1);
    try { return await fn(); } finally { setBusyCount(c => c - 1); }
  }, []);

  const [showBlockModal, setShowBlockModal] = useState(false);
  /** The Find-next trigger's rect while its panel is open; null when closed. */
  const [findNextAt, setFindNextAt] = useState<DOMRect | null>(null);
  /** Overflow "+N" button contents, when a column ran out of lanes. */
  const [more, setMore] = useState<{ m: Extract<CalItem, { kind: 'more' }>; rect: DOMRect } | null>(null);
  /** The card, run or row whose details the side card is showing. */
  const [selected, setSelected] = useState<CalItem | null>(null);
  /** Today opens on the present; the earlier hours collapse into a band. */
  const [showPast, setShowPast] = useState(false);

  const [drag, setDrag] = useState<DragState | null>(null);
  const [colUnit, setColUnit] = useState(MIN_COL_WIDTH);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const colsRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLDivElement | null>(null);
  /** Set by a day-strip click so the scroll survives expanding the past band. */
  const pendingHour = useRef<number | null>(null);

  // Opens on the first branch as before; "All branches" is an explicit choice
  // and is never overwritten once the user has picked it.
  const branchPicked = useRef(false);
  useEffect(() => {
    if (!branchPicked.current && !branchId && branches.length) setBranchId(branches[0].id);
  }, [branches, branchId]);

  const resolvedGymId = branches.find(b => b.id === branchId)?.gymId || branches[0]?.gymId || gymId;

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchDay = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    setError(null);
    await track(async () => {
      try {
        const headers = await authHeaders();
        if (!headers) return;
        // withCheckins powers the live cards' "14/17 checked in"; withAttendee
        // gives a 1:1 slot the member's name instead of "1/1".
        const sessionsUrl = `${API_BASE_URL}/api/class-sessions?dateFrom=${dateStr}&dateTo=${dateStr}&withAttendee=1&withCheckins=1${branchId ? `&branchId=${branchId}` : ''}${resolvedGymId ? `&gymId=${resolvedGymId}` : ''}`;
        const blockedUrl = resolvedGymId ? `${API_BASE_URL}/api/blocked-time?gymId=${resolvedGymId}&date=${dateStr}` : null;
        const [sessRes, blockRes] = await Promise.all([
          fetch(sessionsUrl, { headers }),
          blockedUrl ? fetch(blockedUrl, { headers }) : Promise.resolve(null),
        ]);
        const sessData = await sessRes.json();
        if (!sessRes.ok || !sessData.success) throw new Error(sessData.error || 'Failed to load sessions');
        setSessions(sessData.data as DaySheetSession[]);
        if (sessData.timezone) setTimezone(sessData.timezone as string);
        if (blockRes) {
          const blockData = await blockRes.json();
          if (blockRes.ok && blockData.success) setBlocked(blockData.data as BlockedTimeRow[]);
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load day sheet');
      } finally {
        if (showSpinner) setLoading(false);
      }
    });
  }, [branchId, dateStr, resolvedGymId, track]);

  useEffect(() => { fetchDay(true); }, [fetchDay]);
  useEffect(() => { if (refreshSignal > 0) fetchDay(false); }, [refreshSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Branch-scoped staff (the shared trainers prop is deduped across branches)
  useEffect(() => {
    let cancelled = false;
    track(async () => {
      try {
        const headers = await authHeaders();
        if (!headers) return;
        const res = await fetch(
          `${API_BASE_URL}/api/trainer-availability/trainers?${branchId ? `branchId=${branchId}&` : ''}${resolvedGymId ? `gymId=${resolvedGymId}` : ''}`,
          { headers }
        );
        const data = await res.json();
        if (!cancelled && res.ok && data.success) setBranchStaff(data.data as DaySheetTrainer[]);
      } catch { /* fall back to trainers prop */ }
    });
    return () => { cancelled = true; };
  }, [branchId, resolvedGymId, track]);

  /* Who is on shift, from the same maths the booking engine uses. Needs a
     single branch — across branches a provider has no one roster. */
  useEffect(() => {
    if (!branchId) { setRoster(null); return; }
    let cancelled = false;
    track(async () => {
      try {
        const headers = await authHeaders();
        if (!headers) return;
        const res = await fetch(
          `${API_BASE_URL}/api/trainer-availability/roster?branchId=${branchId}&date=${dateStr}`,
          { headers }
        );
        const data = await res.json();
        if (!cancelled && res.ok && data.success) setRoster(data.data as DayRoster);
        else if (!cancelled) setRoster(null);
      } catch { if (!cancelled) setRoster(null); }
    });
    return () => { cancelled = true; };
  }, [branchId, dateStr, track]);

  // "Now" line, live cards and "in N min" all move off this.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => tzNow(timezone), [timezone, nowTick]);
  const isToday = dateStr === now.dateStr;
  /** The grid is in the DOM — anything that measures it has to wait for this. */
  const ready = !loading && !error;

  /* A front desk leaves this open all shift. Refresh the day itself while it is
     today's day, so a booking taken at the counter shows up here. */
  useEffect(() => {
    if (!isToday) return;
    const t = setInterval(() => fetchDay(false), 45_000);
    return () => clearInterval(t);
  }, [isToday, fetchDay]);

  // ── Day model ────────────────────────────────────────────────────────────────

  const staffList = branchStaff ?? trainers;
  const branchName = useCallback(
    (id?: string) => {
      const b = branches.find(x => x.id === id);
      return b ? (b.displayName || b.name) : undefined;
    },
    [branches]
  );
  const branchRooms = useMemo(
    () => rooms
      .filter(r => !branchId || r.branchId === branchId)
      .sort((a, b) => (a.kind || '').localeCompare(b.kind || '') || a.name.localeCompare(b.name)),
    [rooms, branchId]
  );
  const rosterById = useMemo(
    () => new Map((roster?.staff || []).map(s => [s.id, s])),
    [roster]
  );

  /** Counts for the type filter: what you would get back by re-enabling a type. */
  const typeCounts = useMemo(() => {
    const c: Partial<Record<BookableType, number>> = {};
    for (const s of sessions) {
      const t = (s.class.bookableType || 'class') as BookableType;
      c[t] = (c[t] || 0) + 1;
    }
    return c;
  }, [sessions]);

  const filteredSessions = useMemo(
    () => sessions.filter(s => types.includes((s.class.bookableType || 'class') as BookableType)),
    [sessions, types]
  );

  const namer = useMemo(() => staffNamer(staffList), [staffList]);

  /** The whole day in calendar shape, before the past band trims it. */
  const dayCal = useMemo(
    () => filteredSessions.map(s => toCalSession(s as unknown as ApiSession, { staffName: namer, now })),
    [filteredSessions, namer, now]
  );

  const [startHour, endHour] = useMemo(() => hourWindow(dayCal, DEFAULT_HOURS), [dayCal]);

  /* Today opens at the half hour before now, so the present is the first thing
     on screen; the hours before it fold into the past band. */
  const h0 = isToday && !showPast
    ? Math.min(Math.max(startHour, Math.floor((now.min - 30) / 60)), Math.max(startHour, endHour - 2))
    : startHour;
  const collapsedHours = h0 - startHour;

  const hourPx = DENSITY[prefs.density];
  const pxPerMin = hourPx / 60;
  const dayStartMin = h0 * 60;
  const dayEndMin = endHour * 60;
  const gridHeight = (endHour - h0) * hourPx;
  const geo: Geometry = { startHour: h0, hourPx };

  const pastDone = useMemo(
    () => dayCal.filter(s => s.status !== 'cancelled' && s.end <= dayStartMin),
    [dayCal, dayStartMin]
  );

  /** Which column a session belongs in on the current axis. */
  const colKeyFor = useCallback((s: CalSession): string => {
    const raw = s.raw as DaySheetSession;
    if (axis === 'staff') {
      if (raw.trainerId && staffList.some(t => t.id === raw.trainerId)) return raw.trainerId;
      // A class with nobody to run it is a problem; a court with nobody to run
      // it is a court. They never share a column.
      return s.staffed ? COL_UNASSIGNED : COL_SELF;
    }
    return raw.roomId && branchRooms.some(r => r.id === raw.roomId) ? raw.roomId : COL_NOROOM;
  }, [axis, staffList, branchRooms]);

  /** Sessions drawn in the grid, keyed by column. The past band's hours are cut. */
  const calByCol = useMemo(() => {
    const out: Record<string, CalSession[]> = {};
    for (const s of dayCal) {
      if (s.end <= dayStartMin) continue;
      (out[colKeyFor(s)] ||= []).push(s);
    }
    return out;
  }, [dayCal, colKeyFor, dayStartMin]);

  /** Every session of the day by column, past included — column totals count
   *  the whole day, not just the part still on screen. */
  const dayByCol = useMemo(() => {
    const out: Record<string, CalSession[]> = {};
    for (const s of dayCal) (out[colKeyFor(s)] ||= []).push(s);
    return out;
  }, [dayCal, colKeyFor]);

  // ── Columns ──────────────────────────────────────────────────────────────────

  const columns = useMemo<DayColumn[]>(() => {
    const allBranches = !branchId;
    const stats = (key: string) => {
      const live = (dayByCol[key] || []).filter(s => s.status !== 'cancelled');
      const booked = live.reduce((a, s) => a + s.booked, 0);
      const cap = live.reduce((a, s) => a + s.cap, 0);
      return { n: live.length, fill: cap ? Math.round((booked / cap) * 100) : 0 };
    };

    if (axis === 'staff') {
      const out: DayColumn[] = [];

      const unassigned = calByCol[COL_UNASSIGNED] || [];
      if (unassigned.length) {
        out.push({
          key: COL_UNASSIGNED,
          kind: 'unassigned',
          label: 'Unassigned',
          sub: `${unassigned.length} need a coach`,
          staffId: null,
        });
      }

      const staffCols = staffList.map<DayColumn>(t => {
        const st = rosterById.get(t.id);
        const role = roleLabel(st?.role || t.role);
        const s = stats(t.id);
        const home = allBranches ? branchName(t.branchIds?.[0]) : undefined;
        const work = st?.onLeave === 'full' ? 'on leave' : s.n ? `${s.n} session${s.n === 1 ? '' : 's'}` : 'free';
        return {
          key: t.id,
          kind: 'staff',
          label: t.displayName || t.name || 'Staff',
          sub: [role, home, work].filter(Boolean).join(' · '),
          staffId: t.id,
          branchId: t.branchIds?.length === 1 ? t.branchIds[0] : undefined,
          staff: st,
          onShift: isToday && !!st && onShiftAt(st, now.min),
        };
      });
      // Busy staff first: an empty column is context, not the headline.
      staffCols.sort((a, b) =>
        ((dayByCol[b.key]?.length || 0) > 0 ? 1 : 0) - ((dayByCol[a.key]?.length || 0) > 0 ? 1 : 0) ||
        a.label.localeCompare(b.label));
      out.push(...staffCols);

      const self = calByCol[COL_SELF] || [];
      if (self.length) {
        out.push({
          key: COL_SELF,
          kind: 'self',
          label: 'Self-serve',
          sub: 'Courts, pool & steam',
          staffId: null,
          wide: true,
        });
      }
      return out;
    }

    const out: DayColumn[] = [];
    const noRoom = calByCol[COL_NOROOM] || [];
    if (noRoom.length) {
      out.push({ key: COL_NOROOM, kind: 'noroom', label: 'No room', sub: `${noRoom.length} without a room`, roomId: null });
    }
    const roomCols = branchRooms.map<DayColumn>(r => {
      const live = (dayByCol[r.id] || []).filter(s => s.status !== 'cancelled');
      const dominant = live.length
        ? [...live].sort((a, b) => CAL_TYPE_ORDER.indexOf(a.type) - CAL_TYPE_ORDER.indexOf(b.type))[0].type
        : undefined;
      const s = stats(r.id);
      const home = allBranches ? branchName(r.branchId) : undefined;
      const kind = r.kind ? r.kind.charAt(0).toUpperCase() + r.kind.slice(1) : undefined;
      return {
        key: r.id,
        kind: 'room',
        label: r.name,
        sub: [dominant ? CAL_TYPES[dominant].label : kind, home, s.n ? `${s.fill}%` : 'free'].filter(Boolean).join(' · '),
        roomId: r.id,
        branchId: r.branchId,
        type: dominant,
      };
    });
    /* Rooms in use first, in type order — the design's ordering. Idle rooms
       stay on the end rather than being dropped: the desk still books into a
       quiet studio by clicking its empty column. */
    roomCols.sort((a, b) => {
      const an = dayByCol[a.key]?.length || 0;
      const bn = dayByCol[b.key]?.length || 0;
      if ((an > 0) !== (bn > 0)) return an > 0 ? -1 : 1;
      const at = a.type ? CAL_TYPE_ORDER.indexOf(a.type) : 99;
      const bt = b.type ? CAL_TYPE_ORDER.indexOf(b.type) : 99;
      return at - bt || a.label.localeCompare(b.label);
    });
    out.push(...roomCols);
    return out;
  }, [axis, staffList, branchRooms, branchId, branchName, calByCol, dayByCol, rosterById, isToday, now.min]);

  /* Column geometry. Self-serve carries four courts side by side, so it gets
     the design's 1.4 share; everything else splits the rest evenly. */
  const colGeom = useMemo(() => {
    const weights = columns.map(c => (c.wide ? SELF_COL_WEIGHT : 1));
    const widths = weights.map(w => Math.round(colUnit * w));
    const lefts: number[] = [];
    let x = 0;
    for (const w of widths) { lefts.push(x); x += w; }
    return { widths, lefts, total: x };
  }, [columns, colUnit]);

  useEffect(() => {
    const measure = () => {
      const w = scrollRef.current?.clientWidth || 0;
      const weight = columns.reduce((a, c) => a + (c.wide ? SELF_COL_WEIGHT : 1), 0);
      if (w && weight) setColUnit(Math.max(MIN_COL_WIDTH, Math.floor((w - AXIS_WIDTH - 1) / weight)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [columns, ready]);

  const colIndexByKey = useMemo(() => {
    const map: Record<string, number> = {};
    columns.forEach((c, i) => { map[c.key] = i; });
    return map;
  }, [columns]);

  /** Back-to-back slots of one bookable merge into a run, so a court that is
   *  open 06:00–10:00 is one bar rather than four unreadable slivers. */
  const positioned = useMemo(() => {
    const out: Record<string, ReturnType<typeof layout>> = {};
    columns.forEach((c, i) => {
      const list = calByCol[c.key];
      if (!list) return;
      out[c.key] = layout(buildItems(list), maxLanesFor(colGeom.widths[i] || MIN_COL_WIDTH, 2));
    });
    return out;
  }, [calByCol, columns, colGeom]);

  /** The row behind a card, for the drag handlers and the detail panel. */
  const rawById = useMemo(() => {
    const m = new Map<string, DaySheetSession>();
    for (const s of filteredSessions) m.set(s.id, s);
    return m;
  }, [filteredSessions]);

  const blockedByCol = useMemo(() => {
    const out: Record<string, BlockedTimeRow[]> = {};
    for (const b of blocked) {
      const key = axis === 'staff' ? b.staffId : b.roomId;
      if (!key || colIndexByKey[key] === undefined) continue;
      (out[key] ||= []).push(b);
    }
    return out;
  }, [blocked, axis, colIndexByKey]);

  // ── Selection ────────────────────────────────────────────────────────────────

  const pick = useCallback((it: CalItem) => { setSelected(it); setMore(null); }, []);
  const pickSession = useCallback(
    (s: CalSession) => pick({ kind: 'one', id: s.id, start: s.start, end: s.end, type: s.type, s }),
    [pick]
  );
  const openRaw = useCallback((s: CalSession) => {
    const raw = rawById.get(s.id);
    if (raw) onSessionClick(raw);
  }, [rawById, onSessionClick]);

  // Esc closes the popover first, then the drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (findNextAt) setFindNextAt(null);
      else if (more) setMore(null);
      else if (selected) setSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [findNextAt, more, selected]);

  const setAxis = (a: 'staff' | 'rooms') => { setPrefs({ axis: a }); setSelected(null); };
  const goDay = (next: string) => { setDateStr(next); setSelected(null); setShowPast(false); };

  // ── Scrolling ────────────────────────────────────────────────────────────────

  /** Which hours the grid is showing — drawn back onto the day strip. */
  const [viewport, setViewport] = useState<[number, number]>([h0, endHour]);

  /* Measured off the DOM rather than derived from scrollTop alone: the sticky
     header and the grid's top gutter both sit above the first hour line, and
     the header also covers that many pixels of whatever is scrolled under it.
     `.a-td-scroll` is the offset parent, so offsetTop is exactly that inset. */
  const gridBox = () => {
    const el = scrollRef.current;
    const grid = colsRef.current;
    if (!el || !grid || !el.clientHeight) return null;
    return { el, gridTop: grid.offsetTop, headH: headRef.current?.clientHeight ?? 0 };
  };

  const syncViewport = useCallback(() => {
    const box = gridBox();
    if (!box) return;
    const { el, gridTop, headH } = box;
    const top = dayStartMin + Math.max(0, el.scrollTop + headH - gridTop) / pxPerMin;
    const bottom = dayStartMin + Math.max(0, el.scrollTop + el.clientHeight - gridTop) / pxPerMin;
    const from = Math.floor(top / 60);
    const to = Math.ceil(bottom / 60);
    setViewport(prev => (prev[0] === from && prev[1] === to ? prev : [from, to]));
  }, [dayStartMin, pxPerMin]);

  const scrollToHour = useCallback((hour: number) => {
    const box = gridBox();
    if (!box) return;
    const { el, gridTop, headH } = box;
    el.scrollTop = Math.max(0, gridTop - headH + (hour * 60 - dayStartMin) * pxPerMin);
  }, [dayStartMin, pxPerMin]);

  /* The day, the axis or the past band changing means a different grid: start
     at the top, unless a day-strip click asked for a particular hour. Density
     is deliberately not in here — rescaling the hours shouldn't throw you back
     to 06:00. */
  const gridKey = `${dateStr}|${axis}|${showPast}`;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const hour = pendingHour.current;
    pendingHour.current = null;
    if (hour === null) el.scrollTop = 0;
    else scrollToHour(hour);
    syncViewport();
  }, [ready, gridKey, scrollToHour, syncViewport]);

  /* Re-measure whenever the box itself changes, not just when it is scrolled:
     the first paint, a window resize, the sidebar collapsing and a density
     change all move which hours are on screen, and none of them scroll. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => syncViewport());
    ro.observe(el);
    return () => ro.disconnect();
  }, [ready, syncViewport]);

  const jumpToHour = (hour: number) => {
    if (hour < h0) {
      // The hour is inside the collapsed past — open it, then scroll there.
      pendingHour.current = hour;
      setShowPast(true);
      return;
    }
    scrollToHour(hour);
  };

  // ── Drag to move ─────────────────────────────────────────────────────────────

  const colAt = useCallback((clientX: number): number => {
    const rect = colsRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const x = clientX - rect.left;
    for (let i = columns.length - 1; i >= 0; i--) if (x >= colGeom.lefts[i]) return i;
    return 0;
  }, [columns.length, colGeom]);

  const beginDrag = (e: React.PointerEvent, s: DaySheetSession) => {
    if (e.button !== 0 || s.status !== 'scheduled') return;
    const cal = dayCal.find(x => x.id === s.id);
    const colIdx = colIndexByKey[cal ? colKeyFor(cal) : ''] ?? 0;
    const startMin = timeToMin(s.startTime);
    const rect = colsRef.current?.getBoundingClientRect();
    if (!rect) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({
      sessionId: s.id,
      durationMin: Math.max(15, timeToMin(s.endTime) - startMin),
      origStartMin: startMin,
      origColIdx: colIdx,
      startClientX: e.clientX,
      startClientY: e.clientY,
      grabOffsetY: e.clientY - (rect.top + (startMin - dayStartMin) * pxPerMin),
      curStartMin: startMin,
      curColIdx: colIdx,
      moved: false,
    });
  };

  const moveDrag = (e: React.PointerEvent, s: DaySheetSession) => {
    if (!drag || drag.sessionId !== s.id) return;
    const rect = colsRef.current?.getBoundingClientRect();
    if (!rect) return;
    const moved = drag.moved || Math.abs(e.clientX - drag.startClientX) + Math.abs(e.clientY - drag.startClientY) > 5;
    if (!moved) return;
    const rawTop = e.clientY - rect.top - drag.grabOffsetY;
    const rawMin = dayStartMin + rawTop / pxPerMin;
    const snapped = Math.round(rawMin / DROP_SNAP_MIN) * DROP_SNAP_MIN;
    const curStartMin = Math.max(dayStartMin, Math.min(dayEndMin - drag.durationMin, snapped));
    setDrag({ ...drag, moved: true, curStartMin, curColIdx: colAt(e.clientX) });
  };

  const endDrag = async (e: React.PointerEvent, s: DaySheetSession) => {
    if (!drag || drag.sessionId !== s.id) return;
    const d = drag;
    setDrag(null);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }

    if (!d.moved) {
      const cal = dayCal.find(x => x.id === s.id);
      if (cal) pickSession(cal);
      return;
    }
    if (d.curStartMin === d.origStartMin && d.curColIdx === d.origColIdx) return;

    const col = columns[d.curColIdx];
    /* Dropping into Unassigned or No room clears that field — an explicit
       "take the coach off this class". Self-serve is not a resource at all:
       there is no id to write, so it is not a drop target. */
    if (!col || col.kind === 'self') return;

    const newStart = minToTime(d.curStartMin);
    const newEnd = minToTime(d.curStartMin + d.durationMin);
    const body: Record<string, unknown> = { startTime: newStart, endTime: newEnd };
    if (d.curColIdx !== d.origColIdx) {
      if (axis === 'staff') body.trainerId = col.staffId ?? null;
      else body.roomId = col.roomId ?? null;
    }

    // Optimistic move; revert on failure
    const prev = sessions;
    setSessions(cur => cur.map(x => x.id === s.id
      ? {
          ...x,
          startTime: newStart,
          endTime: newEnd,
          ...(body.trainerId !== undefined ? { trainerId: body.trainerId as string | null } : {}),
          ...(body.roomId !== undefined
            ? { roomId: body.roomId as string | null, room: body.roomId ? { id: body.roomId as string, name: col.label } : null }
            : {}),
        }
      : x));

    try {
      const headers = await authHeaders();
      if (!headers) throw new Error('Not signed in');
      const res = await track(() => fetch(`${API_BASE_URL}/api/class-sessions/${s.id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }));
      const data = await res.json();
      if (!res.ok || !data.success) {
        setSessions(prev);
        toast.error(data.error || 'Could not move session');
        return;
      }
      toast.success('Session moved');
      fetchDay(false);
    } catch (err: unknown) {
      setSessions(prev);
      toast.error(err instanceof Error ? err.message : 'Could not move session');
    }
  };

  // ── Empty-space click → quick book ───────────────────────────────────────────

  const clickEmpty = (e: React.MouseEvent<HTMLDivElement>, col: DayColumn) => {
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const min = dayStartMin + (e.clientY - rect.top) / pxPerMin;
    const snapped = Math.max(dayStartMin, Math.min(dayEndMin - CLICK_SNAP_MIN, Math.floor(min / CLICK_SNAP_MIN) * CLICK_SNAP_MIN));
    onQuickBook({
      branchId: branchId || col.branchId,
      date: dateStr,
      startTime: minToTime(snapped),
      staffId: col.staffId || undefined,
      roomId: col.roomId || undefined,
    });
  };

  const deleteBlocked = async (b: BlockedTimeRow) => {
    if (!window.confirm(`Remove this blocked time${b.reason ? ` (${b.reason})` : ''}?`)) return;
    try {
      const headers = await authHeaders();
      if (!headers) return;
      const res = await track(() => fetch(`${API_BASE_URL}/api/blocked-time/${b.id}`, { method: 'DELETE', headers }));
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to remove');
      toast.success('Blocked time removed');
      fetchDay(false);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove');
    }
  };

  // ── Side-card actions ────────────────────────────────────────────────────────

  const patchSession = async (id: string, body: Record<string, unknown>, ok: string) => {
    try {
      const headers = await authHeaders();
      if (!headers) return;
      const res = await track(() => fetch(`${API_BASE_URL}/api/class-sessions/${id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }));
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Update failed');
      toast.success(ok);
      fetchDay(false);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    }
  };

  /** On-shift providers with nothing booked across this session. */
  const freeStaffFor = useCallback((s: CalSession): RosterStaff[] => {
    if (!roster) return [];
    const busyIds = new Set(
      dayCal
        .filter(x => x.status !== 'cancelled' && x.start < s.end && x.end > s.start)
        .map(x => (x.raw as DaySheetSession).trainerId)
        .filter(Boolean) as string[]
    );
    return roster.staff.filter(st =>
      !busyIds.has(st.id) &&
      st.onLeave !== 'full' &&
      st.shifts.some(w => w.start <= s.start && w.end >= s.end));
  }, [roster, dayCal]);

  // ── Render ───────────────────────────────────────────────────────────────────

  const hourMarks = useMemo(() => {
    const marks: number[] = [];
    for (let m = dayStartMin; m <= dayEndMin; m += 60) marks.push(m);
    return marks;
  }, [dayStartMin, dayEndMin]);

  const nowOnScreen = isToday && now.min >= dayStartMin && now.min <= dayEndMin;

  return (
    <div className="acal a-td flex-1 flex flex-col min-h-0">
      {/* Toolbar */}
      <div className="border-b bg-gray-50 px-4 sm:px-6 py-2.5">
        <div className="a-bar">
          <div className="a-wnav">
            <button className="a-ic" onClick={() => goDay(addDaysStr(dateStr, -1))} aria-label="Previous day">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span>
              <input
                type="date"
                value={dateStr}
                onChange={e => { if (e.target.value) goDay(e.target.value); }}
                className="a-dateinput"
                aria-label="Date"
              />
              <em>{fmtDateLabel(dateStr)}</em>
            </span>
            <button className="a-ic" onClick={() => goDay(addDaysStr(dateStr, 1))} aria-label="Next day">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button className="a-pill" onClick={() => goDay(localDateStr(new Date()))}>Today</button>

          <BranchSelect
            value={branchId}
            onChange={v => { branchPicked.current = true; setBranchId(v); setSelected(null); }}
            branches={branches}
          />
          <TypeFilter types={types} onChange={onTypesChange} counts={typeCounts} />

          <Segmented
            value={axis}
            onChange={setAxis}
            options={[['staff', 'Staff'], ['rooms', 'Rooms']]}
            ariaLabel="Group columns by"
          />
          <Segmented
            value={prefs.density}
            onChange={d => setPrefs({ density: d })}
            options={(Object.keys(DENSITY) as Density[]).map(k => [k, k] as [Density, string])}
            small
            ariaLabel="Row density"
          />

          {busy && <BusyPill label="Refreshing…" />}
          <div className="a-sp" />

          <button
            className="a-btn lime"
            /* Measure before the updater runs: React has nulled `currentTarget`
               by the time a lazy setState callback is invoked. */
            onClick={e => {
              const rect = e.currentTarget.getBoundingClientRect();
              setFindNextAt(prev => (prev ? null : rect));
            }}
            aria-expanded={!!findNextAt}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Find next available
          </button>
          <button
            className="a-btn"
            onClick={() => {
              if (!branchId) return void toast.error('Pick a branch to block time in');
              setShowBlockModal(true);
            }}
          >
            <Ban className="w-3.5 h-3.5" />
            Block time
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && !loading && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8">
          <AlertCircle className="w-8 h-8 text-red-400" />
          <p className="text-sm text-gray-600">{error}</p>
          <button
            onClick={() => fetchDay(true)}
            className="px-4 py-2 text-sm font-semibold text-white bg-zinc-900 rounded-xl hover:bg-zinc-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !error && (
        <div className="flex-1 p-4 sm:p-6 space-y-3 overflow-hidden">
          <div className="flex gap-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex-1 h-8 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-50 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {!loading && !error && (
        <div className="a-td-body">
          <section className="a-card a-td-main">
            <TodayDayStrip
              sessions={dayCal}
              startHour={startHour}
              endHour={endHour}
              now={now.min}
              isToday={isToday}
              viewport={viewport}
              onJump={jumpToHour}
            />

            {/* Collapsed past (today only) */}
            {isToday && !showPast && collapsedHours > 0 && (
              <button className="a-pastband" onClick={() => setShowPast(true)}>
                <span>
                  Earlier today · {String(startHour).padStart(2, '0')}:00–{String(h0).padStart(2, '0')}:00
                </span>
                <b>
                  {pastDone.length} session{pastDone.length === 1 ? '' : 's'} done ·{' '}
                  {pastDone.reduce((a, s) => a + s.booked, 0)} members
                </b>
                <em>Show ▾</em>
              </button>
            )}
            {isToday && showPast && startHour < Math.floor((now.min - 30) / 60) && (
              <button className="a-pastband open" onClick={() => setShowPast(false)}>
                <span>Showing the full day</span>
                <em>Collapse earlier hours ▴</em>
              </button>
            )}

            <div ref={scrollRef} className="a-td-scroll" onScroll={syncViewport}>
              <div style={{ width: AXIS_WIDTH + colGeom.total, minWidth: '100%' }}>
                {/* Sticky column headers — a colour square, the resource, then
                    what it runs and how full it is today. */}
                <div className="a-td-head" ref={headRef}>
                  <div className="a-td-corner" style={{ width: AXIS_WIDTH }} />
                  {columns.map((c, i) => {
                    const dot =
                      c.kind === 'unassigned' ? 'oklch(0.55 0.19 25)'
                      : c.kind === 'self' || c.kind === 'noroom' ? '#C9CDD2'
                      : c.kind === 'room' ? (c.type ? CAL_TYPES[c.type].c : '#D5D8DC')
                      : c.onShift ? 'oklch(0.62 0.15 150)' : '#D5D8DC';
                    const round = c.kind === 'staff' || c.kind === 'unassigned';
                    return (
                      <div
                        key={c.key}
                        className={`a-tl-h res${c.kind === 'unassigned' ? ' un' : ''}`}
                        style={{ width: colGeom.widths[i], position: 'static' }}
                      >
                        <span className="rn">
                          <i style={{ background: dot, borderRadius: round ? '50%' : 3 }} />
                          {c.label}
                          {c.onShift && <em className="a-onshift">on shift</em>}
                        </span>
                        <span className="ds">{c.sub}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Body */}
                <div className="flex" style={{ paddingTop: GRID_GUTTER, paddingBottom: GRID_GUTTER }}>
                  {/* Time axis */}
                  <div className="a-tl-gut shrink-0 border-r border-gray-100" style={{ width: AXIS_WIDTH, height: gridHeight, zIndex: 20 }}>
                    {hourMarks.map(m => (
                      <span key={m} style={{ top: (m - dayStartMin) * pxPerMin }}>{minToTime(m)}</span>
                    ))}
                    {nowOnScreen && (
                      <span className="nowlbl" style={{ top: (now.min - dayStartMin) * pxPerMin }}>{minToTime(now.min)}</span>
                    )}
                  </div>

                  {/* Columns */}
                  <div ref={colsRef} className="relative" style={{ width: colGeom.total, height: gridHeight }}>
                    {/* Horizontal time lines */}
                    {hourMarks.map(m => (
                      <div
                        key={m}
                        className="absolute left-0 right-0 border-t border-gray-200 pointer-events-none"
                        style={{ top: (m - dayStartMin) * pxPerMin }}
                      />
                    ))}

                    {columns.map((col, colIdx) => (
                      <div
                        key={col.key}
                        onClick={e => clickEmpty(e, col)}
                        className="absolute top-0 border-l border-gray-100 first:border-l-0 cursor-pointer"
                        style={{ left: colGeom.lefts[colIdx], width: colGeom.widths[colIdx], height: gridHeight }}
                        title="Click an empty slot to quick-book"
                      >
                        {/* Off-shift hours — hatched, and not a drop target. */}
                        {col.staff && offShiftGaps(col.staff, dayStartMin, dayEndMin).map(g => {
                          const h = (g.end - g.start) * pxPerMin;
                          return (
                            <div
                              key={g.start}
                              className="a-offshift"
                              style={{ top: (g.start - dayStartMin) * pxPerMin, height: h }}
                              aria-hidden="true"
                            >
                              {h > 40 && <span>{col.staff?.onLeave === 'full' ? 'On leave' : 'Off shift'}</span>}
                            </div>
                          );
                        })}

                        {/* Blocked time */}
                        {(blockedByCol[col.key] || []).map(b => {
                          const s = Math.max(dayStartMin, timeToMin(b.startTime));
                          const e2 = Math.min(dayEndMin, timeToMin(b.endTime));
                          if (e2 <= dayStartMin || s >= dayEndMin) return null;
                          return (
                            <div
                              key={b.id}
                              onClick={ev => ev.stopPropagation()}
                              className="absolute left-1 right-1 rounded-lg border border-gray-300 bg-gray-100 overflow-hidden z-[5] group"
                              style={{ top: (s - dayStartMin) * pxPerMin, height: Math.max(18, (e2 - s) * pxPerMin), ...HATCH_STYLE }}
                            >
                              <div className="flex items-start justify-between gap-1 px-1.5 py-1">
                                <p className="text-[10px] font-semibold text-gray-600 truncate">
                                  {b.reason || 'Blocked'} · {b.startTime}–{b.endTime}
                                </p>
                                <button
                                  onClick={() => deleteBlocked(b)}
                                  className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove blocked time"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        {/* Sessions — the design's card anatomy, and a run card
                            wherever back-to-back slots of one bookable merged. */}
                        {(positioned[col.key] || []).map(p => {
                          const style = laneStyle(p, geo);
                          const laneW = (colGeom.widths[colIdx] - 8) / p.lanes;

                          if (p.it.kind === 'more') {
                            return <MoreButton key={p.it.id} more={p.it} style={style} onOpen={(m, rect) => setMore({ m, rect })} />;
                          }
                          if (p.it.kind === 'run') {
                            return (
                              <RunCard
                                key={p.it.id}
                                run={p.it}
                                style={style}
                                width={laneW}
                                selected={selected?.id === p.it.id}
                                onPick={() => pick(p.it)}
                              />
                            );
                          }

                          const raw = rawById.get(p.it.s.id);
                          if (!raw) return null;
                          const isDragging = drag?.moved && drag.sessionId === raw.id;
                          const draggable = raw.status === 'scheduled';
                          const item = p.it;
                          return (
                            <EventCard
                              key={item.id}
                              s={item.s}
                              style={style}
                              width={laneW}
                              selected={selected?.id === item.id}
                              extraClass={`${draggable ? 'a-grab' : ''} ${isDragging ? 'dragging' : ''}`.trim()}
                              onActivate={() => pick(item)}
                              interaction={{
                                onPointerDown: ev => beginDrag(ev as React.PointerEvent, raw),
                                onPointerMove: ev => moveDrag(ev as React.PointerEvent, raw),
                                onPointerUp: ev => endDrag(ev as React.PointerEvent, raw),
                                onPointerCancel: () => setDrag(null),
                                style: { touchAction: 'none', userSelect: 'none', zIndex: 10 },
                              }}
                            />
                          );
                        })}
                      </div>
                    ))}

                    {/* Now line — across every column, no dot in this view */}
                    {nowOnScreen && (
                      <div className="a-tl-now" style={{ top: (now.min - dayStartMin) * pxPerMin, zIndex: 15 }} aria-hidden="true" />
                    )}

                    {/* Drag ghost */}
                    {drag?.moved && (() => {
                      const s = sessions.find(x => x.id === drag.sessionId);
                      if (!s) return null;
                      const T = calType(s.class.bookableType || 'class');
                      return (
                        <div
                          className="a-ev"
                          style={{
                            ...typeVars(T),
                            top: (drag.curStartMin - dayStartMin) * pxPerMin,
                            height: Math.max(24, drag.durationMin * pxPerMin),
                            left: colGeom.lefts[drag.curColIdx] + 4,
                            width: colGeom.widths[drag.curColIdx] - 8,
                            boxShadow: '0 0 0 2px #121417, 0 8px 20px rgba(0,0,0,.18)',
                            zIndex: 40,
                            pointerEvents: 'none',
                          }}
                        >
                          <span className="a-ev-t">{s.class.name}</span>
                          <span className="a-ev-m">
                            {minToTime(drag.curStartMin)}–{minToTime(drag.curStartMin + drag.durationMin)} · {columns[drag.curColIdx]?.label}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {!columns.length && (
                  <div className="a-tl-empty">Nothing scheduled with the current filters.</div>
                )}
              </div>
            </div>
          </section>

          <aside className={`a-card a-td-side${selected ? ' sel' : ''}`}>
            {selected ? (
              <SessionDrawer
                it={selected}
                dateLabel={fmtDateLabel(dateStr)}
                onClose={() => setSelected(null)}
                onViewBookings={openRaw}
                onEdit={openRaw}
              />
            ) : (
              <TodaySide
                sessions={dayCal}
                isToday={isToday}
                now={now.min}
                onPick={pickSession}
                onAssign={(s, staffId) => patchSession(s.id, { trainerId: staffId }, 'Coach assigned')}
                onAddSpot={s => patchSession(s.id, { capacity: s.cap + 1 }, 'One more spot opened')}
                onPromote={openRaw}
                freeStaffFor={freeStaffFor}
              />
            )}
          </aside>
        </div>
      )}

      {findNextAt && (
        <FindNextPopover
          classes={classes}
          gymId={resolvedGymId}
          branchId={branchId}
          dateStr={dateStr}
          timezone={timezone}
          rect={findNextAt}
          onPickSlot={p => onQuickBook(p)}
          onPickSession={s => onSessionClick(s)}
          onClose={() => setFindNextAt(null)}
        />
      )}

      {more && (
        <MorePopover
          more={more.m}
          rect={more.rect}
          onPick={it => { if (it.kind !== 'more') pick(it); }}
          onClose={() => setMore(null)}
        />
      )}

      {/* Block time modal */}
      {showBlockModal && (
        <BlockTimeModal
          gymId={resolvedGymId}
          branchId={branchId}
          dateStr={dateStr}
          staff={staffList}
          rooms={branchRooms}
          defaultTarget={axis === 'rooms' ? 'room' : 'staff'}
          onClose={() => setShowBlockModal(false)}
          onSaved={() => fetchDay(false)}
        />
      )}
    </div>
  );
}
